import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import {
    Explore as CompassIcon,
    Speed as AirspeedIcon,
    Thermostat as BaroIcon,
    Memory as ImuIcon,
    HelpOutline,
    Tune as TuneIcon,
} from '@mui/icons-material';
import { getFixTypeName } from '../mavlink/mavlinkHelper';
import AttitudeIndicator from './AttitudeIndicator';

// ArduPilot AP_HAL::Device id encoding
// bits 0-2: bus_type, bits 3-7: bus, bits 8-15: address, bits 16-23: devtype
const BUS_TYPE_NAMES = { 1: 'I2C', 2: 'SPI', 3: 'CAN', 4: 'SITL', 5: 'MSP', 6: 'SERIAL' };
export const decodeDevId = (raw) => {
    const id = Number(raw) >>> 0;
    if (!id) return null;
    const busType = id & 0x7;
    const bus = (id >> 3) & 0x1F;
    const address = (id >> 8) & 0xFF;
    const devtype = (id >> 16) & 0xFF;
    return { busType, bus, address, devtype, name: BUS_TYPE_NAMES[busType] || `BUS${busType}` };
};
const formatDevId = (info) => {
    if (!info) return null;
    if (info.busType === 1) return `${info.name}${info.bus} 0x${info.address.toString(16).toUpperCase().padStart(2,'0')}`;
    if (info.busType === 2) return `${info.name}${info.bus}`;
    if (info.busType === 3) return `${info.name}${info.bus} N${info.address}`;
    return `${info.name}${info.bus}`;
};

// ArduPilot DEVTYPE enums per sensor category
const IMU_DEVTYPES = {
    0x09: 'BMI160', 0x10: 'L3G4200D', 0x11: 'LSM303D', 0x12: 'BMA180', 0x13: 'MPU6000',
    0x16: 'MPU9250', 0x17: 'IIS328DQ', 0x18: 'LSM9DS1',
    0x21: 'MPU6000', 0x22: 'L3GD20', 0x24: 'MPU9250', 0x25: 'I3G4250D', 0x26: 'LSM9DS1',
    0x27: 'ICM20789', 0x28: 'ICM20689', 0x29: 'BMI055', 0x2A: 'SITL', 0x2B: 'BMI088',
    0x2C: 'ICM20948', 0x2D: 'ICM20648', 0x2E: 'ICM20649', 0x2F: 'ICM20602', 0x30: 'ICM20601',
    0x31: 'ADIS1647x', 0x32: 'SERIAL', 0x33: 'ICM40609', 0x34: 'ICM42688', 0x35: 'ICM42605',
    0x36: 'ICM40605', 0x37: 'IIM42652', 0x38: 'BMI270', 0x39: 'BMI085', 0x3A: 'ICM42670',
    0x3B: 'ICM45686', 0x3C: 'SCHA63T', 0x3D: 'IIM42653', 0x3E: 'LSM6DSV', 0x3F: 'ASM330',
    0x40: 'ADIS16607', 0x41: 'ZEROONE_FPGA_SCH16T',
};
const COMPASS_DEVTYPES = {
    0x01: 'HMC5883(old)', 0x02: 'LSM303D', 0x04: 'AK8963', 0x05: 'BMM150', 0x06: 'LSM9DS1',
    0x07: 'HMC5883', 0x08: 'LIS3MDL', 0x09: 'AK09916', 0x0A: 'IST8310', 0x0B: 'ICM20948',
    0x0C: 'MMC3416', 0x0D: 'QMC5883L', 0x0E: 'MAG3110', 0x0F: 'SITL', 0x10: 'IST8308',
    0x11: 'RM3100', 0x12: 'RM3100_2', 0x13: 'MMC5983', 0x14: 'AK09918', 0x15: 'AK09915',
    0x16: 'QMC5883P', 0x17: 'BMM350', 0x18: 'IIS2MDC', 0x19: 'LIS2MDL',
};;
const BARO_DEVTYPES = {
    0x01: 'SITL', 0x02: 'BMP085', 0x03: 'BMP280', 0x04: 'BMP388', 0x05: 'DPS280',
    0x06: 'DPS310', 0x07: 'FBM320', 0x08: 'ICM20789', 0x09: 'KellerLD', 0x0A: 'LPS2XH',
    0x0B: 'MS5611', 0x0C: 'SPL06', 0x0D: 'UAVCAN', 0x0E: 'MSP', 0x0F: 'ICP101XX',
    0x10: 'ICP201XX', 0x11: 'MS5607', 0x12: 'MS5837_30BA', 0x13: 'MS5637', 0x14: 'BMP390',
    0x15: 'BMP581', 0x16: 'SPA06', 0x17: 'AUAV', 0x18: 'MS5837_02BA',
};
const AIRSPEED_DEVTYPES = {
    0x01: 'SITL', 0x02: 'MS4525', 0x03: 'MS5525', 0x04: 'DLVR', 0x05: 'MSP', 0x06: 'SDP3X',
    0x07: 'UAVCAN', 0x08: 'Analog', 0x09: 'NMEA', 0x0A: 'ASP5033', 0x0B: 'AUAV',
};

const devtypeName = (info, table) => {
    if (!info || !table) return null;
    return table[info.devtype] || null;
};
export const formatSubtitle = (info, table, fallback) => {
    if (!info) return fallback;
    const bus = formatDevId(info);
    const model = devtypeName(info, table) || `devtype 0x${info.devtype.toString(16).toUpperCase().padStart(2,'0')}`;
    if (model && bus) return `${model} · ${bus}`;
    return model || bus || fallback;
};
export const getParamValue = (mavId, name) => {
    const list = window.paramList && window.paramList[mavId];
    if (!list || !list[name]) return null;
    return list[name].value;
};

// MAV_SYS_STATUS_SENSOR bitmasks
const SENSOR_3D_GYRO = 1;
const SENSOR_3D_ACCEL = 2;
const SENSOR_3D_MAG = 4;
const SENSOR_ABSOLUTE_PRESSURE = 8;
const SENSOR_DIFFERENTIAL_PRESSURE = 16;
export const SENSOR_GPS = 32;
export const SENSOR_BATTERY = 0x20000000;

export const sumVoltages = (voltages) => {
    if (!voltages) return 0;
    let v = 0;
    for (let i = 0; i < voltages.length; i++) {
        if (voltages[i] !== 65535) v += voltages[i];
    }
    return v;
};

export const SensorStatus = ({ present, enabled, healthy }) => {
    if (!present) {
        return <Chip size="small" label="Absent" variant="outlined" sx={{ height: 20, fontSize: 11 }} />;
    }
    if (!healthy) {
        return <Chip size="small" color="error" label="Unhealthy" sx={{ height: 20, fontSize: 11 }} />;
    }
    if (!enabled) {
        return <Chip size="small" color="warning" label="Disabled" sx={{ height: 20, fontSize: 11 }} />;
    }
    return <Chip size="small" color="success" label="Healthy" sx={{ height: 20, fontSize: 11 }} />;
};

export const SensorCard = ({ icon, title, subtitle, status, rows, extra, tooltip }) => {
    const theme = useTheme();
    const card = (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.25,
                minWidth: 200,
                flex: '1 1 220px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                bgcolor: 'background.default',
                containerType: 'inline-size',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{title}</Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
                    )}
                </Box>
                {status}
            </Stack>
            {(rows && rows.length > 0) || extra ? (
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    {rows && rows.length > 0 && (
                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'auto 1fr auto', columnGap: 1, rowGap: 0.25, alignItems: 'baseline' }}>
                            {rows.map((r, i) => (
                                <React.Fragment key={i}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.label}</Typography>
                                    <Typography variant="caption" sx={{ textAlign: 'right', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontVariantNumeric: 'tabular-nums', whiteSpace: 'pre' }}>{r.value}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 32, textAlign: 'left' }}>{r.unit || ''}</Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                    {extra && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                '@container (max-width: 300px)': { display: 'none' },
                            }}
                        >
                            {extra}
                        </Box>
                    )}
                </Stack>
            ) : null}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} arrow placement="top">{card}</Tooltip> : card;
};

export const sensorState = (sysStatus, mask) => {
    if (!sysStatus) return { present: false, enabled: false, healthy: false };
    return {
        present: Boolean(sysStatus.onboard_control_sensors_present & mask),
        enabled: Boolean(sysStatus.onboard_control_sensors_enabled & mask),
        healthy: Boolean(sysStatus.onboard_control_sensors_health & mask),
    };
};

export const SectionHeader = ({ title, actions }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
            sx={{
                fontSize: 11,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: 'text.secondary',
            }}
        >
            {title}
        </Typography>
        {actions ? <Box sx={{ display: 'flex', gap: 0.5 }}>{actions}</Box> : null}
    </Box>
);

const Sensors = ({ selectedMavId, mavTimestamp, paramListTimestamp }) => {
    const [sysStatus, setSysStatus] = useState(null);
    const [rawImu, setRawImu] = useState(null);
    const [scaledImu2, setScaledImu2] = useState(null);
    const [scaledImu3, setScaledImu3] = useState(null);
    const [pressure1, setPressure1] = useState(null);
    const [pressure2, setPressure2] = useState(null);
    const [pressure3, setPressure3] = useState(null);
    const [vfrHud, setVfrHud] = useState(null);
    const [airspeedMsgs, setAirspeedMsgs] = useState({});

    // ---- Calibration state ----
    // mode: null | 'gyro' | 'baro' | 'accel-full' | 'accel-level' | 'board-level' | 'airspeed'
    const [calMode, setCalMode] = useState(null);
    const [calLog, setCalLog] = useState([]);
    const [calPosIdx, setCalPosIdx] = useState(0); // next position 0..5 for accel-full
    const calLogRef = useRef([]);
    const ACCEL_POSITIONS = ['LEVEL', 'LEFT', 'RIGHT', 'NOSEDOWN', 'NOSEUP', 'BACK'];

    useEffect(() => {
        if (!calMode) return;
        const handler = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            const text = (d.text || '').replace(/\0+$/g, '').trim();
            if (!text) return;
            calLogRef.current = [...calLogRef.current, text].slice(-40);
            setCalLog(calLogRef.current);
        };
        window.addEventListener('STATUSTEXT', handler);
        return () => window.removeEventListener('STATUSTEXT', handler);
    }, [calMode, selectedMavId]);

    const startCalibration = (mode) => {
        calLogRef.current = [];
        setCalLog([]);
        setCalPosIdx(0);
        setCalMode(mode);
        switch (mode) {
            case 'gyro':        window.api.calibrateGyro(selectedMavId); break;
            case 'baro':        window.api.calibrateBaro(selectedMavId); break;
            case 'accel-full':  window.api.calibrateAccelFull(selectedMavId); break;
            case 'accel-level': window.api.calibrateAccelLevel(selectedMavId); break;
            case 'board-level': window.api.calibrateBoardLevel(selectedMavId); break;
            case 'airspeed':    window.api.calibrateAirspeed(selectedMavId); break;
            default: break;
        }
    };

    const cancelCalibrationFlow = () => {
        if (selectedMavId != null) window.api.cancelCalibration(selectedMavId);
        setCalMode(null);
    };

    const sendNextAccelPos = () => {
        const pos = calPosIdx + 1; // 1-based
        if (pos < 1 || pos > 6) return;
        window.api.accelCalVehiclePos(selectedMavId, pos);
        setCalPosIdx(calPosIdx + 1);
    };

    const calTitle = ({
        'gyro': 'Gyro Calibration',
        'baro': 'Barometer Calibration',
        'accel-full': 'Accel Calibration (6-Position)',
        'accel-level': 'Accel Level Calibration',
        'board-level': 'Board Level Calibration',
        'airspeed': 'Airspeed Calibration',
    })[calMode] || 'Calibration';

    // ---- Compass (mag) calibration ----
    const [magCalOpen, setMagCalOpen] = useState(false);
    const [magCalProgress, setMagCalProgress] = useState({}); // {compass_id: {pct, status, attempt}}
    const [magCalReports, setMagCalReports] = useState({});   // {compass_id: report}
    const [magCalAcks, setMagCalAcks] = useState([]);         // [{cmd, result, ts}]
    const [magCalStatusTexts, setMagCalStatusTexts] = useState([]);

    const MAG_CAL_CMD_NAMES = { 42424: 'START_MAG_CAL', 42425: 'ACCEPT_MAG_CAL', 42426: 'CANCEL_MAG_CAL' };
    const MAV_RESULT_NAMES = {
        0: 'ACCEPTED', 1: 'TEMPORARILY_REJECTED', 2: 'DENIED', 3: 'UNSUPPORTED',
        4: 'FAILED', 5: 'IN_PROGRESS', 6: 'CANCELLED',
    };

    useEffect(() => {
        if (!magCalOpen) return;
        const onProgress = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            setMagCalProgress((p) => ({
                ...p,
                [d.compass_id]: { pct: d.completion_pct, status: d.cal_status, attempt: d.attempt },
            }));
        };
        const onReport = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            setMagCalReports((r) => ({ ...r, [d.compass_id]: d }));
        };
        const onAck = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            if (!MAG_CAL_CMD_NAMES[d.command]) return;
            setMagCalAcks((a) => [...a, { cmd: d.command, result: d.result }].slice(-10));
        };
        const onStatus = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            const text = (d.text || '').replace(/\0+$/g, '').trim();
            if (!text) return;
            // Only keep mag-related status lines to reduce noise.
            if (!/mag|compass|calibrat/i.test(text)) return;
            setMagCalStatusTexts((s) => [...s, text].slice(-20));
        };
        window.addEventListener('MAG_CAL_PROGRESS', onProgress);
        window.addEventListener('MAG_CAL_REPORT', onReport);
        window.addEventListener('COMMAND_ACK', onAck);
        window.addEventListener('STATUSTEXT', onStatus);
        return () => {
            window.removeEventListener('MAG_CAL_PROGRESS', onProgress);
            window.removeEventListener('MAG_CAL_REPORT', onReport);
            window.removeEventListener('COMMAND_ACK', onAck);
            window.removeEventListener('STATUSTEXT', onStatus);
        };
    }, [magCalOpen, selectedMavId]);

    const MAG_CAL_STATUS_NAMES = {
        0: 'Not Started', 1: 'Waiting', 2: 'Step 1', 3: 'Step 2',
        4: 'Success', 5: 'Failed', 6: 'Bad Orientation', 7: 'Bad Radius',
    };

    const startMagCalibration = () => {
        setMagCalProgress({});
        setMagCalReports({});
        setMagCalAcks([]);
        setMagCalStatusTexts([]);
        setMagCalOpen(true);
        // autosave=0 so user must Accept; otherwise FC saves and ignores later ACCEPT.
        window.api.startMagCal(selectedMavId, { mask: 0, retry: 1, autosave: 0, delay: 0, autoreboot: 0 });
    };
    const retryMagCalibration = () => {
        // Stop any in-progress calibration session, then start fresh.
        window.api.cancelMagCal(selectedMavId, 0xFF);
        setMagCalProgress({});
        setMagCalReports({});
        setMagCalAcks([]);
        setMagCalStatusTexts([]);
        setMagCalOpen(true);
        window.api.startMagCal(selectedMavId, { mask: 0, retry: 1, autosave: 0, delay: 0, autoreboot: 0 });
    };
    const acceptMagCalibration = () => {
        // Build mask from successful reports; fall back to all known instances if none yet.
        const successIds = Object.entries(magCalReports)
            .filter(([, r]) => r && r.cal_status === 4)
            .map(([id]) => Number(id));
        const allIds = Array.from(new Set([
            ...Object.keys(magCalReports).map(Number),
            ...Object.keys(magCalProgress).map(Number),
        ]));
        const ids = successIds.length ? successIds : allIds;
        let mask = ids.reduce((m, id) => m | (1 << id), 0);
        if (!mask) mask = 0x07; // fallback: all three slots
        console.log('[MagCal] Accept mask=0x' + mask.toString(16));
        window.api.acceptMagCal(selectedMavId, mask);
    };
    const cancelMagCalibration = () => { window.api.cancelMagCal(selectedMavId, 0xFF); setMagCalOpen(false); };

    useEffect(() => {
        if (!window.mav) return;
        const vehicle = window.mav[selectedMavId];
        if (!vehicle) return;
        if (vehicle.SYS_STATUS) setSysStatus(vehicle.SYS_STATUS);
        if (vehicle.RAW_IMU) setRawImu(vehicle.RAW_IMU);
        if (vehicle.SCALED_IMU2) setScaledImu2(vehicle.SCALED_IMU2);
        if (vehicle.SCALED_IMU3) setScaledImu3(vehicle.SCALED_IMU3);
        if (vehicle.SCALED_PRESSURE) setPressure1(vehicle.SCALED_PRESSURE);
        if (vehicle.SCALED_PRESSURE2) setPressure2(vehicle.SCALED_PRESSURE2);
        if (vehicle.SCALED_PRESSURE3) setPressure3(vehicle.SCALED_PRESSURE3);
        if (vehicle.VFR_HUD) setVfrHud(vehicle.VFR_HUD);
        if (vehicle.AIRSPEED) {
            const a = vehicle.AIRSPEED;
            setAirspeedMsgs((prev) => {
                if (prev[a.id] === a) return prev;
                return { ...prev, [a.id]: a };
            });
        }
    }, [mavTimestamp, selectedMavId]);

    const magCards = () => {
        const compasses = [
            { name: 'Compass 1', mag: rawImu ? { x: rawImu.xmag, y: rawImu.ymag, z: rawImu.zmag } : null, devIdParam: 'COMPASS_DEV_ID' },
            { name: 'Compass 2', mag: scaledImu2 ? { x: scaledImu2.xmag, y: scaledImu2.ymag, z: scaledImu2.zmag } : null, devIdParam: 'COMPASS_DEV_ID2' },
            { name: 'Compass 3', mag: scaledImu3 ? { x: scaledImu3.xmag, y: scaledImu3.ymag, z: scaledImu3.zmag } : null, devIdParam: 'COMPASS_DEV_ID3' },
        ];
        const magStatus = sensorState(sysStatus, SENSOR_3D_MAG);
        const prioIds = [
            Number(getParamValue(selectedMavId, 'COMPASS_PRIO1_ID')) >>> 0,
            Number(getParamValue(selectedMavId, 'COMPASS_PRIO2_ID')) >>> 0,
            Number(getParamValue(selectedMavId, 'COMPASS_PRIO3_ID')) >>> 0,
        ];
        return compasses.map((c, idx) => {
            const devIdRaw = getParamValue(selectedMavId, c.devIdParam);
            const devIdNum = Number(devIdRaw) >>> 0;
            // SCALED_IMU2/3 also carries IMU data even when no compass is attached, so
            // require both a non-zero DEV_ID parameter and non-zero mag readings.
            const hasMag = c.mag != null && (c.mag.x !== 0 || c.mag.y !== 0 || c.mag.z !== 0);
            const present = devIdNum !== 0 && hasMag;
            const strength = present ? Math.sqrt(c.mag.x ** 2 + c.mag.y ** 2 + c.mag.z ** 2) : null;
            const info = decodeDevId(devIdRaw);
            const subtitle = formatSubtitle(info, COMPASS_DEVTYPES, 'metadata missing');
            const prioIdx = devIdNum ? prioIds.findIndex((p) => p && p === devIdNum) : -1;
            const prioLabel = prioIdx >= 0 ? `Prio ${prioIdx + 1}` : null;
            return (
                <SensorCard
                    key={c.name}
                    icon={<CompassIcon fontSize="small" />}
                    title={prioLabel ? `${c.name} · ${prioLabel}` : c.name}
                    subtitle={subtitle}
                    status={<SensorStatus
                        present={present}
                        enabled={idx === 0 ? magStatus.enabled : present}
                        healthy={idx === 0 ? magStatus.healthy : present}
                    />}
                    rows={present ? [
                        { label: 'X', value: `${c.mag.x}`, unit: 'mG' },
                        { label: 'Y', value: `${c.mag.y}`, unit: 'mG' },
                        { label: 'Z', value: `${c.mag.z}`, unit: 'mG' },
                        { label: '|B|', value: `${strength.toFixed(0)}`, unit: 'mG' },
                    ] : [{ label: '—', value: 'No data' }]}
                />
            );
        });
    };

    const baroCards = () => {
        const baros = [
            { name: 'Baro 1', data: pressure1, devIdParam: 'BARO1_DEVID' },
            { name: 'Baro 2', data: pressure2, devIdParam: 'BARO2_DEVID' },
            { name: 'Baro 3', data: pressure3, devIdParam: 'BARO3_DEVID' },
        ];
        const baroStatus = sensorState(sysStatus, SENSOR_ABSOLUTE_PRESSURE);
        return baros.map((b, idx) => {
            const present = b.data != null;
            const info = decodeDevId(getParamValue(selectedMavId, b.devIdParam));
            const subtitle = formatSubtitle(info, BARO_DEVTYPES, 'metadata missing');
            return (
                <SensorCard
                    key={b.name}
                    icon={<BaroIcon fontSize="small" />}
                    title={b.name}
                    subtitle={subtitle}
                    status={<SensorStatus
                        present={present}
                        enabled={idx === 0 ? baroStatus.enabled : present}
                        healthy={idx === 0 ? baroStatus.healthy : present}
                    />}
                    rows={present ? [
                        { label: 'Abs', value: `${b.data.press_abs.toFixed(2)}`, unit: 'hPa' },
                        { label: 'Diff', value: `${b.data.press_diff.toFixed(3)}`, unit: 'hPa' },
                        { label: 'Temp', value: `${(b.data.temperature / 100).toFixed(1)}`, unit: '°C' },
                    ] : [{ label: '—', value: 'No data' }]}
                />
            );
        });
    };

    const airspeedCards = () => {
        const status = sensorState(sysStatus, SENSOR_DIFFERENTIAL_PRESSURE);
        const groundspeed = vfrHud ? vfrHud.groundspeed : null;
        const sensors = [
            { name: 'Airspeed 1', devIdParam: 'ARSPD_DEVID', id: 0, fallbackPress: pressure1 },
            { name: 'Airspeed 2', devIdParam: 'ARSPD2_DEVID', id: 1, fallbackPress: null },
        ];
        return sensors.map((s, idx) => {
            const devIdRaw = getParamValue(selectedMavId, s.devIdParam);
            const info = decodeDevId(devIdRaw);
            const subtitle = formatSubtitle(info, AIRSPEED_DEVTYPES, 'metadata missing');
            const msg = airspeedMsgs[s.id];
            const hasParam = devIdRaw != null && Number(devIdRaw) !== 0;
            const present = msg != null || hasParam || (idx === 0 && (status.present || (s.fallbackPress && s.fallbackPress.press_diff !== 0)));
            const airspeed = msg ? msg.airspeed : (idx === 0 && vfrHud ? vfrHud.airspeed : null);
            const diff = msg ? msg.diff_pressure : (s.fallbackPress ? s.fallbackPress.press_diff : null);
            const temp = msg ? msg.temperature : null;
            const tempValid = temp != null && Number.isFinite(temp) && temp !== 0 && temp !== 32767 && Math.abs(temp) < 1e6;
            const tempC = tempValid ? temp / 100 : null;
            // AIRSPEED_SENSOR_FLAGS: 1=UNHEALTHY, 2=USING
            const hasFlags = msg && msg.flags != null;
            const flagUnhealthy = hasFlags && (msg.flags & 1) !== 0;
            const flagUsing = hasFlags && (msg.flags & 2) !== 0;
            const rows = present ? [
                { label: 'Airspeed', value: airspeed != null ? airspeed.toFixed(2) : '—', unit: 'm/s' },
                ...(idx === 0 ? [{ label: 'Ground', value: groundspeed != null ? groundspeed.toFixed(2) : '—', unit: 'm/s' }] : []),
                { label: 'Diff P.', value: diff != null ? diff.toFixed(3) : '—', unit: 'hPa' },
                { label: 'Temp', value: tempC != null ? tempC.toFixed(1) : '—', unit: tempC != null ? '°C' : '' },
                ...(hasFlags ? [{ label: 'Using', value: flagUsing ? 'Yes' : 'No' }] : []),
            ] : [{ label: '—', value: 'No data' }];
            const healthy = hasFlags
                ? !flagUnhealthy
                : (idx === 0 ? (status.healthy || present) : present);
            const enabled = hasFlags
                ? flagUsing
                : (idx === 0 ? (status.enabled || present) : present);
            return (
                <SensorCard
                    key={s.name}
                    icon={<AirspeedIcon fontSize="small" />}
                    title={s.name}
                    subtitle={subtitle}
                    status={<SensorStatus
                        present={present}
                        enabled={enabled}
                        healthy={healthy}
                    />}
                    rows={rows}
                />
            );
        });
    };

    const imuCards = () => {
        const imus = [
            { name: 'IMU 1', data: rawImu, devIdParam: 'INS_ACC_ID' },
            { name: 'IMU 2', data: scaledImu2, devIdParam: 'INS_ACC2_ID' },
            { name: 'IMU 3', data: scaledImu3, devIdParam: 'INS_ACC3_ID' },
        ];
        const gyroStatus = sensorState(sysStatus, SENSOR_3D_GYRO);
        const accelStatus = sensorState(sysStatus, SENSOR_3D_ACCEL);
        return imus
            .map((imu, idx) => ({ ...imu, idx, devIdRaw: getParamValue(selectedMavId, imu.devIdParam) }))
            .map((imu) => {
                const { idx } = imu;
                const d = imu.data;
                const present = d != null;
                const info = decodeDevId(imu.devIdRaw);
                const subtitle = formatSubtitle(info, IMU_DEVTYPES, 'metadata missing');
                const tempC = present && d && d.temperature != null && d.temperature !== 0 ? (d.temperature / 100).toFixed(1) : null;
                const healthy = idx === 0 ? (gyroStatus.healthy && accelStatus.healthy) : present;
                const enabled = idx === 0 ? (gyroStatus.enabled && accelStatus.enabled) : present;
                const sensorPresent = idx === 0 ? (gyroStatus.present || accelStatus.present || present) : present;
                const padNum = (n, w = 5) => String(n ?? '').padStart(w, ' ');
                const fmt3 = (a, b, c) => `X:${padNum(a)}  Y:${padNum(b)}  Z:${padNum(c)}`;
                return (
                    <SensorCard
                        key={imu.name}
                        icon={<ImuIcon fontSize="small" />}
                        title={imu.name}
                        subtitle={subtitle}
                        status={<SensorStatus present={sensorPresent} enabled={enabled} healthy={healthy} />}
                        rows={present ? [
                            { label: 'Accel', value: fmt3(d.xacc, d.yacc, d.zacc), unit: 'mG' },
                            { label: 'Gyro', value: fmt3(d.xgyro, d.ygyro, d.zgyro), unit: 'mrad/s' },
                            ...(tempC != null ? [{ label: 'Temp', value: tempC, unit: '°C' }] : []),
                        ] : [{ label: '—', value: 'No data' }]}
                        extra={present ? (
                            <AttitudeIndicator
                                selectedMavId={selectedMavId}
                                mavTimestamp={mavTimestamp}
                                size={72}
                                showBearingMarkers={false}
                            />
                        ) : null}
                    />
                );
            });
    };


    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Box sx={{ flex: 1, minHeight: 0 }} style={{ display: 'flex', flexDirection: 'column', margin: 2 }}>
                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2, overflow: 'auto' }}>
                    <Box>
                        <SectionHeader
                            title="IMU"
                            actions={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                        Calibration
                                    </Typography>
                                    <ButtonGroup size="small" variant="outlined" disabled={selectedMavId == null}>
                                        <Button startIcon={<TuneIcon fontSize="inherit" />} onClick={() => startCalibration('accel-full')}>Accel</Button>
                                        <Button onClick={() => startCalibration('board-level')}>Level</Button>
                                    </ButtonGroup>
                                </Box>
                            )}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {imuCards()}
                        </Box>
                    </Box>
                    <Box>
                        <SectionHeader
                            title="Compass"
                            actions={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                        Calibration
                                    </Typography>
                                    <ButtonGroup size="small" variant="outlined" disabled={selectedMavId == null}>
                                        <Button startIcon={<TuneIcon fontSize="inherit" />} onClick={startMagCalibration}>Start</Button>
                                    </ButtonGroup>
                                </Box>
                            )}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {magCards()}
                        </Box>
                    </Box>
                    <Box>
                        <SectionHeader title="Barometer" />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {baroCards()}
                        </Box>
                    </Box>
                    <Box>
                        <SectionHeader title="Airspeed" />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {airspeedCards()}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Dialog open={calMode != null} onClose={cancelCalibrationFlow} maxWidth="sm" fullWidth>
                <DialogTitle>{calTitle}</DialogTitle>
                <DialogContent dividers>
                    {calMode === 'accel-full' && (
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Place vehicle in each orientation and press Next.
                            </Typography>
                            <Typography variant="body2">
                                Next position: <b>{calPosIdx < 6 ? `${calPosIdx + 1}. ${ACCEL_POSITIONS[calPosIdx]}` : 'Done — waiting for result'}</b>
                            </Typography>
                        </Box>
                    )}
                    <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', backgroundColor: 'action.hover' }}>
                        {calLog.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Waiting for vehicle status…</Typography>
                        ) : (
                            calLog.map((line, i) => (
                                <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{line}</Typography>
                            ))
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Button onClick={() => window.api.reboot(selectedMavId)} color="error" variant="contained"
                        disabled={selectedMavId == null}>
                        Reboot
                    </Button>
                    <Box>
                        {calMode === 'accel-full' && (
                            <Button onClick={sendNextAccelPos} variant="contained" disabled={calPosIdx >= 6}>
                                {calPosIdx >= 6 ? 'All Sent' : `Next: ${ACCEL_POSITIONS[calPosIdx]}`}
                            </Button>
                        )}
                        <Button onClick={cancelCalibrationFlow} color="warning">Cancel</Button>
                        <Button onClick={() => setCalMode(null)}>Close</Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog open={magCalOpen} onClose={() => setMagCalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Compass Calibration</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Rotate the vehicle slowly around all axes until each compass reaches 100%.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Fitness (mGauss RMS, lower is better): &lt;4 Very Good · &lt;8 Good · &lt;15 OK · &lt;25 Poor · ≥25 Bad. ArduPilot default acceptance threshold: 16.
                    </Typography>
                    {Object.keys(magCalProgress).length === 0 && Object.keys(magCalReports).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Waiting for compass progress…</Typography>
                    ) : (
                        Array.from(new Set([...Object.keys(magCalProgress), ...Object.keys(magCalReports)])).sort().map((id) => {
                            const p = magCalProgress[id];
                            const r = magCalReports[id];
                            const pct = r ? 100 : (p ? p.pct : 0);
                            const status = r ? r.cal_status : (p ? p.status : 0);
                            const fitnessNum = r && r.fitness != null ? r.fitness : null;
                            const fitness = fitnessNum != null ? fitnessNum.toFixed(2) : (r ? '—' : null);
                            // ArduPilot compass-cal fitness rating thresholds (milligauss RMS residual).
                            // Lower is better; default acceptance threshold COMPASS_CAL_FIT = 16.
                            let fitnessRating = null;
                            let fitnessColor = 'text.secondary';
                            if (fitnessNum != null) {
                                if (fitnessNum < 4)       { fitnessRating = 'Very Good'; fitnessColor = 'success.main'; }
                                else if (fitnessNum < 8)  { fitnessRating = 'Good';      fitnessColor = 'success.main'; }
                                else if (fitnessNum < 15) { fitnessRating = 'OK';        fitnessColor = 'warning.main'; }
                                else if (fitnessNum < 25) { fitnessRating = 'Poor';      fitnessColor = 'warning.main'; }
                                else                       { fitnessRating = 'Bad';       fitnessColor = 'error.main';   }
                            }
                            // Map instance compass_id (0..2) to dev id & priority slot.
                            const devIdParams = ['COMPASS_DEV_ID', 'COMPASS_DEV_ID2', 'COMPASS_DEV_ID3'];
                            const prioParams = ['COMPASS_PRIO1_ID', 'COMPASS_PRIO2_ID', 'COMPASS_PRIO3_ID'];
                            const instIdx = Number(id);
                            const instDevId = (Number(getParamValue(selectedMavId, devIdParams[instIdx])) >>> 0) || 0;
                            const prioIdx = instDevId ? prioParams.findIndex((n) => (Number(getParamValue(selectedMavId, n)) >>> 0) === instDevId) : -1;
                            const label = `Compass ${instIdx + 1}` + (prioIdx >= 0 ? ` · Prio ${prioIdx + 1}` : '');
                            return (
                                <Box key={id} sx={{ mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2">{label}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {MAG_CAL_STATUS_NAMES[status] || status}
                                            {fitness != null && (
                                                <>
                                                    {' · fitness '}
                                                    <Box component="span" sx={{ color: fitnessColor, fontWeight: 500 }}>
                                                        {fitness}{fitnessRating ? ` (${fitnessRating})` : ''}
                                                    </Box>
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: 8, backgroundColor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                                        <Box sx={{
                                            width: `${Math.max(0, Math.min(100, pct))}%`,
                                            height: '100%',
                                            backgroundColor: status === 4 ? 'success.main' : (status >= 5 ? 'error.main' : 'primary.main'),
                                            transition: 'width 200ms',
                                        }} />
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                    {(magCalStatusTexts.length > 0 || magCalAcks.length > 0) && (
                        <Paper variant="outlined" sx={{ p: 1, mt: 1, maxHeight: 140, overflow: 'auto', backgroundColor: 'action.hover' }}>
                            {magCalStatusTexts.map((t, i) => (
                                <Typography key={`s${i}`} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{t}</Typography>
                            ))}
                            {magCalAcks.map((a, i) => (
                                <Typography key={`a${i}`} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: a.result === 0 ? 'success.main' : 'warning.main' }}>
                                    {`ACK ${MAG_CAL_CMD_NAMES[a.cmd] || a.cmd} → ${MAV_RESULT_NAMES[a.result] || a.result}`}
                                </Typography>
                            ))}
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Button onClick={() => window.api.reboot(selectedMavId)} color="error" variant="contained"
                        disabled={selectedMavId == null}>
                        Reboot
                    </Button>
                    <Box>
                        <Button onClick={acceptMagCalibration} variant="contained" color="success">
                            Accept
                        </Button>
                        <Button onClick={retryMagCalibration} variant="outlined" sx={{ ml: 1 }} disabled={selectedMavId == null}>
                            Retry
                        </Button>
                        <Button onClick={cancelMagCalibration} color="warning">Cancel</Button>
                        <Button onClick={() => setMagCalOpen(false)}>Close</Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Sensors;
